import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Search, Loader2, Package } from "lucide-react";
import { listTnProductsAll, type TnProduct } from "../../lib/tiendaNube";

// Picker de un producto de Tiendanube (para "agregar a otro producto"). Trae
// TODOS los productos de la tienda (sin dedup), excluye el actual, con buscador.
export function TnProductPicker({
  brandId,
  excludeTnProductId,
  onSelect,
  onClose,
}: {
  brandId: string | null;
  excludeTnProductId: number | null;
  onSelect: (p: { id: number; nombre: string }) => void;
  onClose: () => void;
}) {
  const [products, setProducts] = useState<TnProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(false);
    listTnProductsAll(brandId)
      .then((ps) => {
        if (!alive) return;
        setProducts(ps.filter((p) => p.id !== excludeTnProductId));
      })
      .catch(() => {
        if (alive) setError(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [brandId, excludeTnProductId, reloadKey]);

  const q = query.toLowerCase().trim();
  const filtered = q
    ? products.filter((p) => p.nombre.toLowerCase().includes(q))
    : products;

  return createPortal(
    <div
      className="fixed inset-0 z-[210] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-surface-card rounded-xl shadow-lg p-5 animate-modal-appear max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[15px] font-semibold text-brand-black/90">
            Agregar a otro producto
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar producto..."
            className="w-full pl-10 pr-3 h-[var(--btn-h)] rounded-md border border-neutral-200 bg-surface-card text-[13px] focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors"
          />
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-5 h-5 animate-spin text-brand-black/30" />
          </div>
        ) : error ? (
          <div className="text-[13px] text-neutral-500 py-8 text-center">
            <p className="mb-3">No se pudieron cargar los productos de tu tienda.</p>
            <button
              type="button"
              onClick={() => setReloadKey((k) => k + 1)}
              className="inline-flex items-center h-8 px-4 rounded-md border border-neutral-200 text-brand-black/80 hover:border-brand-primary text-[13px] font-medium transition-colors"
            >
              Reintentar
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-[13px] text-neutral-500 py-8 text-center">
            No hay productos.
          </div>
        ) : (
          <div className="overflow-y-auto grid grid-cols-3 gap-2.5">
            {filtered.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => onSelect({ id: p.id, nombre: p.nombre })}
                className="text-left rounded-lg border border-black/[0.08] bg-surface-card hover:border-brand-primary p-1.5 transition-colors"
              >
                <div className="aspect-square rounded-md overflow-hidden bg-neutral-100 mb-1">
                  {p.img_src ? (
                    <img
                      src={p.img_src}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-5 h-5 text-brand-black/25" />
                    </div>
                  )}
                </div>
                <div className="text-[11.5px] text-brand-black/80 truncate px-0.5">
                  {p.nombre}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

export default TnProductPicker;
